import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  SafetyTest,
  SafetyTestWithQuestions,
  SafetyTestCreateRequest,
  SafetyTestUpdateRequest,
  TestQuestion,
  TestQuestionCreateRequest,
  TestQuestionUpdateRequest,
  TestVersionHistory,
  TestAttempt,
  TestAnalytics
} from '../models/safety-test.model';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SafetyTestService {
  private readonly apiUrl = `${environment.apiUrl}/admin/safety-tests`;

  constructor(private http: HttpClient) {}

  // ============================================
  // TEST CRUD
  // ============================================

  /**
   * List all tests with optional filters
   */
  async listTests(filters?: {
    categoryId?: string;
    status?: string;
    organizationId?: string;
  }): Promise<SafetyTest[]> {
    let url = this.apiUrl;
    const params = new URLSearchParams();

    if (filters?.categoryId) params.append('categoryId', filters.categoryId);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.organizationId) params.append('organizationId', filters.organizationId);

    const queryString = params.toString();
    if (queryString) url += `?${queryString}`;

    const response = await firstValueFrom(
      this.http.get<ApiResponse<SafetyTest[]>>(url)
    );

    return response.success && response.data ? response.data : [];
  }

  /**
   * Get a single test with questions
   */
  async getTest(id: string): Promise<SafetyTestWithQuestions | null> {
    const response = await firstValueFrom(
      this.http.get<ApiResponse<SafetyTestWithQuestions>>(`${this.apiUrl}/${id}`)
    );

    return response.success && response.data ? response.data : null;
  }

  /**
   * Create a new test (as draft)
   */
  async createTest(
    data: SafetyTestCreateRequest,
    questions?: TestQuestionCreateRequest[]
  ): Promise<SafetyTest | null> {
    const response = await firstValueFrom(
      this.http.post<ApiResponse<SafetyTest>>(this.apiUrl, {
        ...data,
        questions
      })
    );

    return response.success && response.data ? response.data : null;
  }

  /**
   * Update an existing test
   */
  async updateTest(id: string, data: SafetyTestUpdateRequest): Promise<SafetyTest | null> {
    const response = await firstValueFrom(
      this.http.put<ApiResponse<SafetyTest>>(`${this.apiUrl}/${id}`, data)
    );

    return response.success && response.data ? response.data : null;
  }

  /**
   * Delete a test (drafts only)
   */
  async deleteTest(id: string): Promise<boolean> {
    const response = await firstValueFrom(
      this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${id}`)
    );

    return response.success;
  }

  // ============================================
  // TEST LIFECYCLE
  // ============================================

  /**
   * Activate a draft test
   */
  async activateTest(id: string): Promise<SafetyTest | null> {
    const response = await firstValueFrom(
      this.http.post<ApiResponse<SafetyTest>>(`${this.apiUrl}/${id}/activate`, {})
    );

    return response.success && response.data ? response.data : null;
  }

  /**
   * Create a new version of an existing test
   */
  async createNewVersion(
    id: string,
    data?: Partial<SafetyTestCreateRequest>
  ): Promise<SafetyTest | null> {
    const response = await firstValueFrom(
      this.http.post<ApiResponse<SafetyTest>>(`${this.apiUrl}/${id}/new-version`, data || {})
    );

    return response.success && response.data ? response.data : null;
  }

  /**
   * Get version history for a test
   */
  async getVersionHistory(id: string): Promise<TestVersionHistory[]> {
    const response = await firstValueFrom(
      this.http.get<ApiResponse<TestVersionHistory[]>>(`${this.apiUrl}/${id}/versions`)
    );

    return response.success && response.data ? response.data : [];
  }

  // ============================================
  // QUESTION MANAGEMENT
  // ============================================

  /**
   * Add a question to a test
   */
  async addQuestion(testId: string, data: TestQuestionCreateRequest): Promise<TestQuestion | null> {
    const response = await firstValueFrom(
      this.http.post<ApiResponse<TestQuestion>>(`${this.apiUrl}/${testId}/questions`, data)
    );

    return response.success && response.data ? response.data : null;
  }

  /**
   * Update a question
   */
  async updateQuestion(
    testId: string,
    questionId: string,
    data: TestQuestionUpdateRequest
  ): Promise<TestQuestion | null> {
    const response = await firstValueFrom(
      this.http.put<ApiResponse<TestQuestion>>(
        `${this.apiUrl}/${testId}/questions/${questionId}`,
        data
      )
    );

    return response.success && response.data ? response.data : null;
  }

  /**
   * Delete a question
   */
  async deleteQuestion(testId: string, questionId: string): Promise<boolean> {
    const response = await firstValueFrom(
      this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${testId}/questions/${questionId}`)
    );

    return response.success;
  }

  /**
   * Reorder questions
   */
  async reorderQuestions(testId: string, questionIds: string[]): Promise<boolean> {
    const response = await firstValueFrom(
      this.http.post<ApiResponse<void>>(`${this.apiUrl}/${testId}/questions/reorder`, {
        questionIds
      })
    );

    return response.success;
  }

  // ============================================
  // STUDY GUIDE
  // ============================================

  /**
   * Upload study guide PDF
   */
  async uploadStudyGuide(testId: string, file: File): Promise<SafetyTest | null> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await firstValueFrom(
      this.http.post<ApiResponse<SafetyTest>>(
        `${this.apiUrl}/${testId}/study-guide`,
        formData
      )
    );

    return response.success && response.data ? response.data : null;
  }

  /**
   * Delete study guide
   */
  async deleteStudyGuide(testId: string): Promise<boolean> {
    const response = await firstValueFrom(
      this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${testId}/study-guide`)
    );

    return response.success;
  }

  // ============================================
  // ATTEMPTS (Admin View)
  // ============================================

  /**
   * List test attempts for an organization
   */
  async listAttempts(organizationId: string, status?: string): Promise<TestAttempt[]> {
    let url = `${this.apiUrl}/test-attempts?organizationId=${organizationId}`;
    if (status) url += `&status=${status}`;

    const response = await firstValueFrom(
      this.http.get<ApiResponse<TestAttempt[]>>(url)
    );

    return response.success && response.data ? response.data : [];
  }

  // ============================================
  // ANALYTICS
  // ============================================

  /**
   * Get test analytics
   */
  async getAnalytics(dateRange?: string): Promise<TestAnalytics | null> {
    let url = `${this.apiUrl}/analytics`;
    if (dateRange) url += `?range=${dateRange}`;

    const response = await firstValueFrom(
      this.http.get<ApiResponse<TestAnalytics>>(url)
    );

    return response.success && response.data ? response.data : null;
  }

  // ============================================
  // CERTIFICATE
  // ============================================

  /**
   * Download certificate PDF for a passed attempt
   */
  async downloadCertificate(attemptId: string): Promise<Blob | null> {
    try {
      const response = await firstValueFrom(
        this.http.get(`${environment.apiUrl}/admin/test-attempts/${attemptId}/certificate`, {
          responseType: 'blob'
        })
      );
      return response;
    } catch (error) {
      console.error('Error downloading certificate:', error);
      return null;
    }
  }

  // ============================================
  // CSV EXPORT/IMPORT
  // ============================================

  /**
   * Export test results as CSV
   */
  async exportResults(testId?: string): Promise<Blob | null> {
    try {
      let url = `${this.apiUrl}/export/results`;
      if (testId) url += `?testId=${testId}`;

      const response = await firstValueFrom(
        this.http.get(url, { responseType: 'blob' })
      );
      return response;
    } catch (error) {
      console.error('Error exporting results:', error);
      return null;
    }
  }

  /**
   * Export questions from a test as CSV
   */
  async exportQuestions(testId: string): Promise<Blob | null> {
    try {
      const response = await firstValueFrom(
        this.http.get(`${this.apiUrl}/${testId}/export/questions`, {
          responseType: 'blob'
        })
      );
      return response;
    } catch (error) {
      console.error('Error exporting questions:', error);
      return null;
    }
  }

  /**
   * Import questions from CSV (preview mode)
   */
  async previewImportQuestions(testId: string, csvData: string): Promise<{
    preview: boolean;
    totalRows: number;
    validRows: number;
    invalidRows: number;
    questions: {
      text: string;
      type: string;
      explanation: string | null;
      options: { text: string; isCorrect: boolean }[];
      isValid: boolean;
      errors: string[];
    }[];
  } | null> {
    const response = await firstValueFrom(
      this.http.post<ApiResponse<{
        preview: boolean;
        totalRows: number;
        validRows: number;
        invalidRows: number;
        questions: {
          text: string;
          type: string;
          explanation: string | null;
          options: { text: string; isCorrect: boolean }[];
          isValid: boolean;
          errors: string[];
        }[];
      }>>(`${this.apiUrl}/${testId}/import/questions`, {
        csvData,
        preview: true
      })
    );

    return response.success && response.data ? response.data : null;
  }

  /**
   * Import questions from CSV (actual import)
   */
  async importQuestions(testId: string, csvData: string, replace = false): Promise<{
    imported: number;
    skipped: number;
    replaced: number;
    questions: TestQuestion[];
  } | null> {
    const response = await firstValueFrom(
      this.http.post<ApiResponse<{
        imported: number;
        skipped: number;
        replaced: number;
        questions: TestQuestion[];
      }>>(`${this.apiUrl}/${testId}/import/questions`, {
        csvData,
        preview: false,
        replace
      })
    );

    return response.success && response.data ? response.data : null;
  }
}
